import React, { useRef, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { toast } from 'react-toastify';
import { InfinitySpin } from 'react-loader-spinner';
import { api_url } from '../../../config';
import api from '../../../utils/api';
import { IoIosArrowDown } from "react-icons/io";

const InventoryForm = ({ suppliers, fetchInventories, toggle }) => {
    const [name, setName] = useState('');
    const [supplierInput, setSupplierInput] = useState('');
    const [description, setDescription] = useState('');
    const [selectedSupplierId, setSelectedSupplierId] = useState(null);
    const [manufacturer, setManufacturer] = useState('');

    const [supplierSuggestions, setSupplierSuggestions] = useState([]);
    const [showSupplierSuggestions, setShowSupplierSuggestions] = useState(false);
    const [loading, setLoading] = useState(false);
    const [publish, setPublish] = useState(false);
    const [error, setError] = useState('');
    const [inventoryStatus, setInventoryStatus] = useState('');
    const [searchText, setSearchText] = useState('');
    const supplierInputRef = useRef(null);
    const { id, branch_id, token } = useSelector((state) => state.auth);
    const searchInputRef = useRef(null);

    useEffect(() => {
        // Close the suggestions dropdown when clicking outside
        const handleClickOutside = (event) => {
            if (supplierInputRef.current && !supplierInputRef.current.contains(event.target)) {
                setShowSupplierSuggestions(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchText(value);

        // Filter suppliers based on the search value
        const filteredSuggestions = suppliers.filter(item =>
            item.name && item.name.toLowerCase().includes(value.toLowerCase())
        );

        setSupplierSuggestions(filteredSuggestions);
    };

    const handleSelectSupplier = (selectedSupplier) => {
        setSupplierInput(selectedSupplier.name);
        setSelectedSupplierId(selectedSupplier.id);
        setShowSupplierSuggestions(false);
        setSearchText(''); // Clear search text when supplier is selected
    };

    const addInventory = async (e) => {
        e.preventDefault();

        if (!name || !selectedSupplierId) {
            setError('Please fill all required fields');
            setTimeout(() => setError(''), 6000);
            return;
        }

        const inventoryDetails = {
            product_name: name,
            supplier_id: selectedSupplierId,
            description,
            public_inventory: publish,
            manufacturer
        };

        try {
            setLoading(true);
            const response = await api.post(api_url + `/api/v1/wholesaler/user/${id}/branch/${branch_id}/inventory`, inventoryDetails, {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`
                }
            });
            fetchInventories();
            setName('');
            setDescription('');
            setSupplierInput('');
            setSelectedSupplierId(null);
            setInventoryStatus('Inventory added successfully');
            toast.success('Inventory added successfully');
            setTimeout(() => {
                toggle();
            }, 2000);
            setLoading(false);
        } catch (error) {
            setLoading(false);
            setError(error?.response?.data?.error || 'Failed adding Inventory');
            setTimeout(() => setError(''), 6000);
            toast.error('Failed adding Inventory');
        }
    };

    const handleFocus = () => {
        // Show all suppliers on focus
        setSupplierSuggestions(suppliers);
        setShowSupplierSuggestions(true);
    };
    const toggleSupplierSuggestions = () => {
        setShowSupplierSuggestions((prev) => {
            const newState = !prev;
            if (newState) {
                // Focus after short delay so it exists in DOM
                setTimeout(() => {
                    searchInputRef.current?.focus();
                }, 0);
            }
            return newState;
        });
    };

    return (
        <div className='max-w-2xl overflow-auto sm:min-w-[300px] lg:min-w-[600px] mx-auto p-4'>
            <div className='mb-2'>
                <p className='font-semibold text-center font-lufga text-neutral-500'>Create New Inventory</p>
            </div>

            <form className="grid gap-4 mb-6 md:grid-cols-2">
                {loading && (
                    <div className="absolute top-0 left-0 z-50 flex items-center justify-center w-full h-full bg-gradient-to-br from-opacity-40 to-opacity-60 backdrop-blur-sm">
                        <InfinitySpin color="#008DDA" size={36} />
                    </div>
                )}
                <div className='flex flex-col gap-3'>
                    <label className="flex items-center gap-1 text-sm font-lufga">Product name<span className='font-serif text-red-400'>*</span></label>
                    <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Enter product name"
                        className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:border-blue-500"
                        required
                    />
                </div>
                <div ref={supplierInputRef} className="relative flex flex-col gap-3">
                    <label className="flex items-center gap-1 text-sm font-lufga">Supplier<span className='font-serif text-red-400'>*</span></label>
                    <div className="relative">
                        <div
                            className="flex items-center justify-between px-3 py-2 bg-white border border-gray-300 rounded-md cursor-pointer"
                            // onClick={() => setShowSupplierSuggestions(!showSupplierSuggestions)}
                            onClick={toggleSupplierSuggestions}

                        >
                            <input
                                type="text"
                                value={supplierInput}
                                className="w-full bg-transparent outline-none cursor-pointer"
                                placeholder="Select supplier..."
                                readOnly
                            />
                            <IoIosArrowDown className={`text-gray-400 transition-transform ${showSupplierSuggestions ? 'rotate-180' : ''}`} />
                        </div>
                        {showSupplierSuggestions && (
                            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-[200px] overflow-auto">
                                <div className="sticky top-0 p-2 bg-white border-b border-gray-200">
                                    <input
                                        ref={searchInputRef}
                                        type="text"
                                        value={searchText}
                                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:border-blue-500"
                                        placeholder="Search supplier..."
                                        onChange={handleSearchChange}
                                        onClick={(e) => e.stopPropagation()}
                                    />
                                </div>
                                {/* <div className="py-1">
                                    {supplierSuggestions.length > 0 ? (
                                        supplierSuggestions.map((supplier, index) => (
                                            <div
                                                key={index}
                                                className="px-3 py-2 text-sm cursor-pointer hover:bg-gray-100"
                                                onClick={() => handleSelectSupplier(supplier)}
                                            >
                                                {supplier.name}
                                            </div>
                                        ))
                                    ) : (
                                        <div className="px-3 py-4 text-sm text-center text-gray-500">
                                            <p>No suppliers available</p>
                                            <p className="mt-1 text-xs">Please add a supplier first to create inventory</p>
                                        </div>
                                    )}
                                </div> */}

                                {searchText.trim() !== '' && (
                                    <div className="py-1">
                                        {supplierSuggestions.length > 0 ? (
                                            supplierSuggestions.map((supplier, index) => (
                                                <div
                                                    key={index}
                                                    className="px-3 py-2 text-sm cursor-pointer hover:bg-gray-100"
                                                    onClick={() => handleSelectSupplier(supplier)}
                                                >
                                                    {supplier.name}
                                                </div>
                                            ))
                                        ) : (
                                            <div className="px-3 py-4 text-sm text-center text-gray-500">
                                                <p>No suppliers match your description</p>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
                <div className='flex flex-col gap-3'>
                    <label className="flex items-center gap-1 text-sm font-lufga">Manufacturer<span className='font-serif text-red-400'>*</span></label>
                    <input
                        type="text"
                        value={manufacturer}
                        onChange={(e) => setManufacturer(e.target.value)}
                        placeholder="Enter manufacturer"
                        className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:border-blue-500"
                        required
                    />
                    <div className='flex items-center justify-start gap-1 '>
                        <input
                            type="checkbox"
                            value={publish}
                            onChange={(e) => setPublish(!publish)}
                            placeholder=""
                            className="w-5 h-5 border border-gray-300 rounded-md focus:outline-none focus:border-blue-500"
                            required
                        />
                        <label className="flex items-center gap-1 text-sm font-lufga">Publish</label>
                    </div>
                </div>
                <div className='flex flex-col gap-3'>
                    <label className="flex items-center gap-1 text-sm font-lufga">Description</label>
                    <textarea
                        type="text"
                        value={description}
                        rows={2}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder="Description"
                        className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:border-blue-500"
                    />
                </div>
            </form>
            <div className='flex items-center justify-between'>
                <div>
                    {inventoryStatus ? <div>
                        <p className="px-2 py-1 text-xs text-center bg-green-300 rounded-md shadow-md font-lufga">{inventoryStatus}</p>
                    </div> : error ? <div>
                        <p className="px-2 py-1 text-sm text-center bg-red-300 rounded-md shadow-md font-lufga ">
                            {typeof error === 'string' ? error : (
                                Object.keys(error).map((key) => (
                                    Array.isArray(error[key]) ? error[key][0] : error[key]
                                ))
                            )}
                        </p>
                    </div> : null}
                </div>
                <button onClick={addInventory} type="submit" className="px-4 py-2 text-white transition duration-300 bg-blue-500 rounded-md shadow-md font-lufga hover:bg-blue-600">Add Inventory</button>
            </div>
        </div>
    );
};

export default InventoryForm;
